/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import { useUser } from "../../../context/AuthContext";
import Multiline from "../../../Components/Masters/Multiline";
import SelectField from "../../../Components/Masters/SelectField";
import MonthYearPicker, { monthlist } from "../../../Components/Common/MonthYearPicker";
import countries from "../../../Pages/Masters/Roles and Permissions/Country.json";
import dayjs from "dayjs";
import { FUEL_TYPE_OPTIONS, FUEL_UNIT_BY_TYPE } from "../../../utils/fuelConstants";

const FieldErrorText = ({ message }) =>
  message ? (
    <Typography variant="body2" color="#D34040">
      {message}
    </Typography>
  ) : null;

const unitMenuProps = {
  PaperProps: {
    style: {
      maxHeight: "55%",
      overflowY: "auto",
    },
  },
};

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
  leasedAssetOptions = [],
  onSearchLeasedAssets = () => { },
}) => {
  const { user } = useUser();

  const isMongoId = (value) =>
    typeof value === "string" && /^[a-f\d]{24}$/i.test(value);

  const toDateInputValue = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

      const dmyMatch = value.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
      if (dmyMatch) {
        const dd = String(dmyMatch[1]).padStart(2, "0");
        const mm = String(dmyMatch[2]).padStart(2, "0");
        const yyyy = dmyMatch[3];
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const parseDateForValidation = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "string") {
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const d = new Date(
          Number(isoMatch[1]),
          Number(isoMatch[2]) - 1,
          Number(isoMatch[3])
        );
        return Number.isNaN(d.getTime()) ? null : d;
      }

      const dmyMatch = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
      if (dmyMatch) {
        const d = new Date(
          Number(dmyMatch[3]),
          Number(dmyMatch[2]) - 1,
          Number(dmyMatch[1])
        );
        return Number.isNaN(d.getTime()) ? null : d;
      }
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const currentCalendarYear = new Date().getFullYear();
  const currentCalendarMonth1 = new Date().getMonth() + 1;
  const staticPickerYears = Array.from(
    { length: 6 },
    (_, i) => currentCalendarYear - 5 + i
  );

  const parseConsumptionMonthToId = (raw) => {
    if (raw === "" || raw === null || raw === undefined) return "";
    const asNum = Number(raw);
    if (Number.isInteger(asNum) && asNum >= 1 && asNum <= 12) return asNum;
    if (typeof raw !== "string") return "";
    const trimmed = raw.trim();
    if (!trimmed) return "";
    const lower = trimmed.toLowerCase();
    const byAbbrev = monthlist.find(
      (m) => m.month.toLowerCase() === lower.slice(0, 3)
    );
    if (byAbbrev) return byAbbrev.id;
    const byLabel = monthlist.find((m) => m.month.toLowerCase() === lower);
    return byLabel ? byLabel.id : "";
  };

  const buildNormalizedConsumptionPeriod = () => {
    const leaseStartDateStr = toDateInputValue(
      initialData?.assetId?.leaseStartDate || initialData?.leaseStartDate
    );
    const leaseEndDateStr = toDateInputValue(
      initialData?.assetId?.leaseEndDate || initialData?.leaseEndDate
    );

    const minDateObj = leaseStartDateStr
      ? parseDateForValidation(leaseStartDateStr)
      : null;
    const maxDateDayjs = leaseEndDateStr
      ? dayjs(leaseEndDateStr).isAfter(dayjs())
        ? dayjs()
        : dayjs(leaseEndDateStr)
      : dayjs();

    const minYear = minDateObj ? minDateObj.getFullYear() : null;
    const maxYear = maxDateDayjs ? maxDateDayjs.year() : null;
    const minMonth = minDateObj ? minDateObj.getMonth() + 1 : null;
    const maxMonth = maxDateDayjs ? maxDateDayjs.month() + 1 : null;
    const hasRangeConstraint = minYear != null && maxYear != null;

    let allowedYears;
    if (hasRangeConstraint) {
      allowedYears = [];
      for (let y = minYear; y <= maxYear; y += 1) {
        allowedYears.push(y);
      }
    } else {
      allowedYears = staticPickerYears;
    }

    const rawYear = initialData?.consumptionPeriod?.year;
    let year = "";
    if (rawYear !== "" && rawYear !== null && rawYear !== undefined) {
      const yNum = Number(rawYear);
      if (Number.isInteger(yNum) && allowedYears.includes(yNum)) {
        year = yNum;
      }
    }

    let month = parseConsumptionMonthToId(initialData?.consumptionPeriod?.month);
    if (month !== "") {
      const yearForRules = year === "" ? null : Number(year);
      let allowedMonthIds = monthlist.map((m) => m.id);
      if (
        !hasRangeConstraint &&
        yearForRules === currentCalendarYear
      ) {
        allowedMonthIds = allowedMonthIds.filter(
          (id) => id <= currentCalendarMonth1
        );
      }
      if (hasRangeConstraint && yearForRules != null) {
        allowedMonthIds = allowedMonthIds.filter((id) => {
          if (yearForRules === minYear && id < minMonth) return false;
          if (yearForRules === maxYear && id > maxMonth) return false;
          return true;
        });
      }
      if (!allowedMonthIds.includes(month)) {
        month = "";
      }
    }

    return { month, year };
  };

  // Safely extract asset size/unit for edit mode from both payload shapes:
  // direct fields (assetSize, assetSizeUnit) and nested master asset (assetId.*)
  const getAssetSizeSource = () => initialData?.assetId?.assetSize;

  const getAssetSizeValue = () => {
    const assetSize = getAssetSizeSource();
    if (assetSize == null || assetSize === "") return "";
    if (typeof assetSize === "object") return assetSize?.value ?? "";
    return assetSize;
  };

  const getAssetSizeUnitValue = () => {
    const assetSize = getAssetSizeSource();
    if (assetSize && typeof assetSize === "object" && assetSize?.unit) {
      return assetSize.unit;
    }
    return initialData?.assetSizeUnit || initialData?.assetId?.assetSizeUnit || "";
  };

  const initialFormData = {
    assetMasterId: isMongoId(initialData?.assetId) ? initialData?.assetId : "",
    assetID: initialData?.assetID || initialData?.assetId || "",
    locationCountry:
      initialData?.assetId?.locationCountry ||
      initialData?.locationCountry ||
      "",
    typeOfAsset: initialData?.assetId?.typeOfAsset || initialData?.assetId?.typeofAsset || "",
    consumptionPeriod: buildNormalizedConsumptionPeriod(),
    leaseStartDate: toDateInputValue(initialData?.assetId?.leaseStartDate || initialData?.leaseStartDate),
    leaseEndDate: toDateInputValue(initialData?.assetId?.leaseEndDate || initialData?.leaseEndDate),
    assetSize: getAssetSizeValue(),
    assetSizeUnit: getAssetSizeUnitValue(),
    electricityConsumption: initialData?.electricityConsumption || "",
    fuelType: initialData?.fuelType || "",
    fuelConsumption: initialData?.fuelConsumption || "",
    fuelConsumptionUnit: initialData?.fuelConsumptionUnit || "",
    remark: initialData?.remark || "",
    editRemarkReason: initialData?.editRemarkReason || "",
  };

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formDataSnapshot, setFormDataSnapshot] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  const [assetSearchText, setAssetSearchText] = useState("");
  const hasSelectedAsset = Boolean(formData?.assetMasterId || formData?.assetID);
  const leaseStartDateForDisable = parseDateForValidation(formData?.leaseStartDate);
  const todayForDisable = new Date();
  const currentMonthValueForDisable =
    todayForDisable.getFullYear() * 100 + (todayForDisable.getMonth() + 1);
  const leaseStartMonthValueForDisable = leaseStartDateForDisable
    ? leaseStartDateForDisable.getFullYear() * 100 +
    (leaseStartDateForDisable.getMonth() + 1)
    : null;
  const disableConsumptionPeriod =
    Boolean(leaseStartMonthValueForDisable) &&
    leaseStartMonthValueForDisable > currentMonthValueForDisable;
  const futureMonthErrorMessage =
    "Consumption Month And Year cannot be accepted in future dates.";
  const disableSubmitForFutureLeaseStart =
    errors?.month === futureMonthErrorMessage ||
    errors?.assetID === futureMonthErrorMessage;
  useEffect(() => {
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles(initialData?.documents || []);
    setAssetSearchText(initialFormData.assetID || "");
  }, [isOpen, initialData]);

  const fieldConfig = [
    {
      label: "Asset ID",
      name: "assetID",
      type: "text",
      required: true,
      placeholder: "Enter Asset ID",
    },
    {
      label: "Country",
      name: "locationCountry",
      type: "text",
      required: false,
      disabled: true,
      placeholder: "Enter country",
    },
    {
      label: "Consumption Month And Year",
      name: "consumptionPeriod",
      type: "monthYear",
      required: true,
    },
    {
      label: "Type of Asset",
      name: "typeOfAsset",
      type: "text",
      required: true,
      placeholder: "Enter type of asset",
      disabled: true,
    },

    {
      label: "Lease Start Date",
      name: "leaseStartDate",
      type: "date",
      required: true,
      disabled: true,
    },
    {
      label: "Lease End Date",
      name: "leaseEndDate",
      type: "date",
      required: true,
      disabled: true,
    },
    {
      label: "Asset Size (sq. ft. or unit count)",
      name: "assetSize",
      type: "text",
      required: true,
      placeholder: "Enter asset size",
      disabled: true,
    },

    {
      label: "Electricity Consumption (in MWH)",
      name: "electricityConsumption",
      type: "number",
      required: false,
      placeholder: "Enter electricity consumption",
    },
    {
      label: "Fuel Name",
      name: "fuelType",
      type: "select",
      required: false,
      options: FUEL_TYPE_OPTIONS,
    },
    {
      label: "Fuel Quantity",
      name: "fuelConsumption",
      type: "number",
      required: false,
      placeholder: "Enter consumption",
    },
    {
      label: "Fuel Unit",
      name: "fuelConsumptionUnit",
      type: "select",
      required: false,
      options: [],
    },

    {
      label: "Remark (optional)",
      name: "remark",
      type: "text",
      required: false,
      placeholder: "Enter remark",
    },
    {
      name: "editRemarkReason",
      type: "multlyline",
      placeholder: "Additional edit remarks",
      require: true,
    },
    {
      name: "document",
      type: "docs",
      placeholder: "Enter Document",
    },
  ];

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles([]);
    onClose();
  };

  const isFormChanged = () =>
    JSON.stringify(formData) !== JSON.stringify(formDataSnapshot);

  const handleClose = () => {
    if (!isFormChanged()) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    const startDate = parseDateForValidation(formData?.leaseStartDate);
    const endDate = parseDateForValidation(formData?.leaseEndDate);

    const today = new Date();

    const todayDateOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    fieldConfig.forEach((field) => {
      if (field?.name === "editRemarkReason") {
        return;
      }

      const value = formData[field?.name];

      const month = formData?.consumptionPeriod?.month;
      const year = formData?.consumptionPeriod?.year;

      // =====================================================
      // Consumption Period Validation
      // =====================================================

      if (field?.name === "consumptionPeriod") {
        if (!hasSelectedAsset) return;

        if (!month) newErrors.month = "Month is required.";

        if (!year) newErrors.year = "Year is required.";

        if (month && year) {
          const selectedMonthValue =
            Number(year) * 100 + Number(month);

          const currentMonthValue =
            todayDateOnly.getFullYear() * 100 +
            (todayDateOnly.getMonth() + 1);

          if (selectedMonthValue > currentMonthValue) {
            newErrors.assetID =
              "Consumption Month And Year cannot be accepted in future dates.";

            return;
          }
        }

        if (month && year && startDate && endDate) {
          const selectedMonthValue =
            Number(year) * 100 + Number(month);

          const leaseStartMonthValue =
            startDate.getFullYear() * 100 +
            (startDate.getMonth() + 1);

          const leaseEndMonthValue =
            endDate.getFullYear() * 100 +
            (endDate.getMonth() + 1);

          if (selectedMonthValue < leaseStartMonthValue) {
            newErrors.month =
              "Consumption Month And Year can not be less than Lease Start Date.";
          } else if (
            selectedMonthValue > leaseEndMonthValue
          ) {
            newErrors.month =
              "Consumption Month And Year can not be greater than Lease End Date.";
          }
        }

        return;
      }

      // =====================================================
      // Skip default required validation
      // handled manually below
      // =====================================================

      if (
        [
          "electricityConsumption",
          "fuelType",
          "fuelConsumption",
          "fuelConsumptionUnit",
          "locationCountry",
        ].includes(field?.name)
      ) {
        return;
      }

      // =====================================================
      // Required Field Validation
      // =====================================================

      if (field?.required) {
        if (
          value === "" ||
          value === null ||
          value === undefined
        ) {
          newErrors[field?.name] =
            `${field?.label} is required.`;

          return;
        }
      }

      // =====================================================
      // Number Validation
      // =====================================================

      if (
        field?.type === "number" &&
        value !== "" &&
        value !== undefined &&
        isNaN(Number(value))
      ) {
        newErrors[field?.name] =
          `${field?.label} must be a number.`;
      }
    });

    // =====================================================
    // Electricity / Fuel Validation
    // =====================================================

    const electricityStr = String(
      formData?.electricityConsumption ?? ""
    ).trim();
    const fuelTypeStr = String(formData?.fuelType ?? "").trim();
    const fuelConsumptionStr = String(
      formData?.fuelConsumption ?? ""
    ).trim();
    const fuelUnitStr = String(
      formData?.fuelConsumptionUnit ?? ""
    ).trim();

    const hasElectricity = electricityStr !== "";
    const hasFuelType = fuelTypeStr !== "";
    const hasFuelConsumption = fuelConsumptionStr !== "";
    const hasFuelUnit = fuelUnitStr !== "";
    const hasAnyFuelField =
      hasFuelType || hasFuelConsumption || hasFuelUnit;

    // Fuel Name activates fuel path (Quantity + Unit required).
    // Electricity-only path applies when KWH is entered and no fuel name is selected.
    const usingFuel =
      hasFuelType ||
      ((hasFuelConsumption || hasFuelUnit) && !hasElectricity);
    const usingElectricity = hasElectricity && !hasFuelType;

    if (!usingElectricity && !usingFuel) {
      newErrors.electricityConsumption =
        "Either Electricity Consumption or Fuel Details are required.";
      newErrors.fuelType =
        "Either Electricity Consumption or Fuel Details are required.";
    }

    if (usingElectricity) {
      const electricityNum = Number(electricityStr);
      if (isNaN(electricityNum) || electricityNum < 0) {
        newErrors.electricityConsumption =
          "Electricity Consumption must be a valid number.";
      }
    }

    if (usingFuel) {
      if (!hasFuelType) {
        newErrors.fuelType = "Fuel Name is required.";
      }
      if (hasFuelType && !hasFuelConsumption) {
        newErrors.fuelConsumption = "Fuel Quantity is required.";
      } else if (
        hasFuelConsumption &&
        (isNaN(Number(fuelConsumptionStr)) || Number(fuelConsumptionStr) < 0)
      ) {
        newErrors.fuelConsumption = "Fuel Quantity must be a valid number.";
      }
      if (hasFuelType && !hasFuelUnit) {
        newErrors.fuelConsumptionUnit = "Fuel Unit is required.";
      }
    }

    if (user?.endUser && isEditing) {
      const remark = String(formData?.editRemarkReason ?? "").trim();
      if (!remark) {
        newErrors.editRemarkReason = "Edit remark is required";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const applySelectedAsset = (selected) => {
    if (!selected) return;
    const selectedLeaseStart = parseDateForValidation(selected.leaseStartDate);
    const today = new Date();
    const currentMonthValue = today.getFullYear() * 100 + (today.getMonth() + 1);
    const leaseStartMonthValue = selectedLeaseStart
      ? selectedLeaseStart.getFullYear() * 100 + (selectedLeaseStart.getMonth() + 1)
      : null;

    setFormData((prev) => ({
      ...prev,
      assetMasterId: selected._id || "",
      assetID: selected.assetId || "",

      typeOfAsset: selected.typeOfAsset || "",
      leaseStartDate: toDateInputValue(selected.leaseStartDate),
      leaseEndDate: toDateInputValue(selected.leaseEndDate),
      consumptionPeriod: { month: "", year: "" },
      assetSize:
        selected.assetSize && typeof selected.assetSize === "object"
          ? selected.assetSize?.value ?? ""
          : selected.assetSize !== undefined && selected.assetSize !== null
            ? String(selected.assetSize)
            : "",
      assetSizeUnit:
        (selected.assetSize && typeof selected.assetSize === "object"
          ? selected.assetSize?.unit
          : "") || selected.assetSizeUnit || "",
      locationCountry: selected?.locationCountry || "",
    }));
    setErrors((prev) => ({
      ...prev,
      // assetID: "",
      typeOfAsset: "",
      leaseStartDate: "",
      leaseEndDate: "",
      assetSize: "",
      assetSizeUnit: "",
      assetID:
        leaseStartMonthValue && leaseStartMonthValue > currentMonthValue
          ? "Consumption Month And Year cannot be accepted in future dates."
          : "",
      year: "",
    }));
  };

  const handleChange = (name, type) => (event) => {
    const { value } = event.target;

    if (type === "number" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    if (name === "assetID") {
      const selected = leasedAssetOptions.find((item) => item?._id === value);
      if (selected) {
        applySelectedAsset(selected);
        setAssetSearchText(selected?.assetId || "");
        return;
      }
    }

    if (name === "electricityConsumption") {
      setFormData((prev) => ({
        ...prev,
        electricityConsumption: value,
      }));
      setErrors((prev) => ({
        ...prev,
        electricityConsumption: "",
        fuelType: "",
        fuelConsumption: "",
        fuelConsumptionUnit: "",
      }));
      return;
    }

    if (name === "fuelType") {
      const cleared = value === "" || value === "Tap to Select";
      if (cleared) {
        setFormData((prev) => ({
          ...prev,
          fuelType: "",
          fuelConsumption: "",
          fuelConsumptionUnit: "",
        }));
        setErrors((prev) => ({
          ...prev,
          fuelType: "",
          fuelConsumption: "",
          fuelConsumptionUnit: "",
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        fuelType: value,
        fuelConsumptionUnit: "",
      }));
      setErrors((prev) => ({
        ...prev,
        fuelType: "",
        fuelConsumptionUnit: "",
        electricityConsumption: "",
        fuelConsumption: "",
      }));
      return;
    }

    if (name === "fuelConsumption" || name === "fuelConsumptionUnit") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        [name]: "",
        electricityConsumption: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;

    let formDataToSubmit = {
      ...formData,
      documents: uploadedFiles,
    };
    if (user?.endUser && isEditing) {
      formDataToSubmit.requestStatus = "edit";
      formDataToSubmit.requestMode = true;
      formDataToSubmit.approvalStatus = "Pending";
    }

    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: "26rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {fieldConfig.map((field) => {
              if (field?.name === "consumptionPeriod" && !hasSelectedAsset) {
                return null;
              }

              if (field?.name === "editRemarkReason" && !(user?.endUser && isEditing)) {
                return null;
              }

              return (
                <div key={field?.name} style={{ margin: "1rem 0" }}>
                  {user?.endUser && isEditing && field?.type === "multlyline" ? (
                    <Multiline
                      title={formatTitleWithOptional("Edit Remark Reason")}
                      placeholder="Additional edit remarks"
                      fieldName="editRemarkReason"
                      value={formData.editRemarkReason}
                      handleChange={handleChange("editRemarkReason")}
                      errors={errors.editRemarkReason}
                    />
                  ) : field?.type === "docs" ? (
                    <FileField
                      title={formatTitleWithOptional("Upload Documents (optional)")}
                      name={field?.name}
                      errors={errors[field?.name]}
                      fileUrls={uploadedFiles}
                      progress={uploadProgress[field?.name] || []}
                      {...{
                        setUploadedFiles,
                        setUploadProgress,
                        setErrors,
                      }}
                    />
                  ) : field?.type === "select" && field?.name === "country" ? (
                    <SelectField
                      title={field?.label}
                      name={field?.name}
                      value={formData[field?.name]}
                      disable={true}
                      handleChange={(e) => {
                        const value = e.target.value === "Tap to select" ? "" : e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          [field?.name]: value,
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          [field?.name]: "",
                        }));
                      }}
                      options={countries.countries.map((country) => country.name)}
                      errors={errors[field?.name]}
                    />
                  ) : field?.type === "monthYear" ? (
                    <MonthYearPicker
                      title={field?.label}
                      formData={formData}
                      setFormData={setFormData}
                      errors={errors}
                      setErrors={setErrors}
                      minDate={formData?.leaseStartDate}
                      maxDate={
                        formData?.leaseEndDate
                          ? (dayjs(formData.leaseEndDate).isAfter(dayjs())
                            ? dayjs()
                            : dayjs(formData.leaseEndDate))
                          : dayjs()
                      }
                      disabled={disableConsumptionPeriod}
                    />
                  ) : field?.type === "select" && field?.name === "fuelType" ? (
                    <SelectField
                      title={field?.label}
                      name={field?.name}
                      value={formData[field?.name]}
                      handleChange={handleChange(field?.name, field?.type)}
                      options={FUEL_TYPE_OPTIONS}
                      errors={errors[field?.name]}
                      allowClear
                    />
                  ) : field?.type === "select" &&
                    field?.name === "fuelConsumptionUnit" ? (
                    <></>
                  ) : field?.name === "assetSize" ? (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                        {field?.label}
                      </Typography>
                      <Box display="flex" gap={1} sx={{ width: "100%" }}>
                        <Box sx={{ width: "60%" }}>
                          <TextField
                            type="text"
                            size="small"
                            fullWidth
                            placeholder={field?.placeholder || ""}
                            value={formData.assetSize || ""}
                            disabled
                            error={Boolean(errors.assetSize)}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "#6b7280",
                                cursor: "not-allowed",
                              },
                              "& .MuiInputBase-root.Mui-disabled": {
                                backgroundColor: "#f3f4f6",
                              },
                            }}
                          />
                        </Box>
                        <Box sx={{ width: "40%" }}>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={formData.assetSizeUnit || ""}
                            disabled
                            SelectProps={{ MenuProps: unitMenuProps, displayEmpty: true }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "#6b7280",
                                cursor: "not-allowed",
                              },
                              "& .MuiInputBase-root.Mui-disabled": {
                                backgroundColor: "#f3f4f6",
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              Unit
                            </MenuItem>
                            {formData.assetSizeUnit ? (
                              <MenuItem value={formData.assetSizeUnit}>
                                {formData.assetSizeUnit}
                              </MenuItem>
                            ) : null}
                          </TextField>
                        </Box>
                      </Box>
                      <FieldErrorText message={errors.assetSize} />
                    </Box>
                  ) : field?.name === "locationCountry" ? (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                        {field?.label}
                      </Typography>
                      <TextField
                        type="text"
                        size="small"
                        fullWidth
                        value={formData.locationCountry || ""}
                        disabled
                        placeholder={field?.placeholder || "Enter country"}
                        sx={{
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#6b7280",
                            cursor: "not-allowed",
                          },
                          "& .MuiInputBase-root.Mui-disabled": {
                            backgroundColor: "#f3f4f6",
                          },
                        }}
                      />
                    </Box>
                  ) : field?.name === "fuelConsumption" ? (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                        {field?.label}
                      </Typography>
                      <Box display="flex" gap={1} sx={{ width: "100%" }}>
                        <Box sx={{ width: "60%" }}>
                          <TextField
                            type="text"
                            size="small"
                            fullWidth
                            placeholder={field?.placeholder || ""}
                            value={formData.fuelConsumption || ""}
                            onChange={handleChange("fuelConsumption", "number")}
                            error={Boolean(errors.fuelConsumption)}
                          />
                          <FieldErrorText message={errors.fuelConsumption} />
                        </Box>
                        <Box sx={{ width: "40%" }}>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            name="fuelConsumptionUnit"
                            value={formData.fuelConsumptionUnit || ""}
                            onChange={handleChange("fuelConsumptionUnit", "select")}
                            disabled={!formData.fuelType}
                            error={Boolean(errors.fuelConsumptionUnit)}
                            SelectProps={{ MenuProps: unitMenuProps, displayEmpty: true }}
                          >
                            <MenuItem value="" disabled>
                              Unit
                            </MenuItem>
                            {(FUEL_UNIT_BY_TYPE[formData.fuelType] || []).map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {unit}
                              </MenuItem>
                            ))}
                          </TextField>
                          <FieldErrorText message={errors.fuelConsumptionUnit} />
                        </Box>
                      </Box>
                    </Box>
                  ) : field?.name === "assetID" ? (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                        {field?.label}
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={leasedAssetOptions}
                        value={
                          leasedAssetOptions.find(
                            (option) =>
                              option?._id === formData.assetMasterId ||
                              option?._id === formData.assetID ||
                              option?.assetId === formData.assetID
                          ) || null
                        }
                        inputValue={assetSearchText}
                        onInputChange={(event, newInputValue, reason) => {
                          setAssetSearchText(newInputValue);
                          if (reason === "input" || reason === "clear") {
                            onSearchLeasedAssets(newInputValue);
                          }
                        }}
                        onChange={(event, newValue) => {
                          applySelectedAsset(newValue);
                          setAssetSearchText(newValue?.assetId || "");
                        }}
                        getOptionLabel={(option) => option?.assetId || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Tap to Select"
                            error={Boolean(errors[field?.name])}
                          />
                        )}
                        sx={{
                          width: "100%",
                          "& .MuiAutocomplete-inputRoot": {
                            height: 40,
                            alignItems: "center",
                          },
                        }}
                      />
                      <FieldErrorText message={errors[field?.name]} />
                    </Box>
                  ) : (
                    <>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mt: 1 }}
                      >
                        {field?.label}
                      </Typography>

                      <TextField
                        type={field?.type === "date" ? "date" : "text"}
                        size="small"
                        fullWidth
                        required={field?.required}
                        multiline={field?.name === "remark"}
                        minRows={field?.name === "remark" ? 3 : undefined}
                        placeholder={field?.placeholder || ""}
                        value={formData[field?.name] || ""}
                        onChange={handleChange(field?.name, field?.type)}
                        disabled={Boolean(field?.disabled)}
                        error={Boolean(errors[field?.name])}
                        sx={{
                          ...(field?.disabled && {
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#6b7280",
                              cursor: "not-allowed",
                            },
                            "& .MuiInputBase-root.Mui-disabled": {
                              backgroundColor: "#f3f4f6",
                            },
                          }),
                        }}
                        inputProps={
                          field?.type === "date"
                            ? { max: new Date().toISOString().split("T")[0] }
                            : {}
                        }
                      />
                      <FieldErrorText message={errors[field?.name]} />
                    </>
                  )}
                </div>
              )
            })}
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={disableSubmitForFutureLeaseStart}
            sx={{ backgroundColor: "#1C486B", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;
